import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import type {
  AnnouncementView, EngagementDashboardView, EngagementSettingView, RecognitionView,
  SurveyQuestionView, SurveyResultView, SurveyView,
} from '../types'

const SURVEYS = '/v1/surveys'
const BASE = '/v1/engagement'

export function useSurveys() {
  return useQuery({
    queryKey: ['engagement', 'surveys'],
    queryFn: async () => (await apiClient.get<SurveyView[]>(SURVEYS)).data,
  })
}

export function useSurvey(id: string | undefined) {
  return useQuery({
    queryKey: ['engagement', 'survey', id],
    enabled: Boolean(id),
    queryFn: async () => (await apiClient.get<SurveyView>(`${SURVEYS}/${id}`)).data,
  })
}

export function useSurveyQuestions(id: string | undefined) {
  return useQuery({
    queryKey: ['engagement', 'survey', id, 'questions'],
    enabled: Boolean(id),
    queryFn: async () =>
      (await apiClient.get<SurveyQuestionView[]>(`${SURVEYS}/${id}/questions`)).data,
  })
}

export function useSurveyResults(id: string | undefined) {
  return useQuery({
    queryKey: ['engagement', 'survey', id, 'results'],
    enabled: Boolean(id),
    queryFn: async () =>
      (await apiClient.get<SurveyResultView>(`${SURVEYS}/${id}/results`)).data,
  })
}

export function useRecognitions() {
  return useQuery({
    queryKey: ['engagement', 'recognition'],
    queryFn: async () => (await apiClient.get<RecognitionView[]>(`${BASE}/recognition`)).data,
  })
}

export function useEngagementSettings() {
  return useQuery({
    queryKey: ['engagement', 'settings'],
    queryFn: async () => (await apiClient.get<EngagementSettingView>(`${BASE}/settings`)).data,
  })
}

export function useAnnouncements() {
  return useQuery({
    queryKey: ['engagement', 'announcements'],
    queryFn: async () => (await apiClient.get<AnnouncementView[]>(`${BASE}/announcements`)).data,
  })
}

export function useEngagementDashboard() {
  return useQuery({
    queryKey: ['engagement', 'dashboard'],
    queryFn: async () =>
      (await apiClient.get<EngagementDashboardView>(`${BASE}/dashboard`)).data,
  })
}

export function useEngagementMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['engagement'] })

  return {
    saveSurvey: useMutation({
      mutationFn: async ({ id, body }: { id?: string; body: Record<string, unknown> }) =>
        id
          ? (await apiClient.put<SurveyView>(`${SURVEYS}/${id}`, body)).data
          : (await apiClient.post<SurveyView>(SURVEYS, body)).data,
      onSuccess: invalidate,
    }),
    addQuestion: useMutation({
      mutationFn: async ({ id, body }: { id: string; body: Record<string, unknown> }) =>
        (await apiClient.post<SurveyQuestionView>(`${SURVEYS}/${id}/questions`, body)).data,
      onSuccess: invalidate,
    }),
    publishSurvey: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<SurveyView>(`${SURVEYS}/${id}/publish`)).data,
      onSuccess: invalidate,
    }),
    respond: useMutation({
      mutationFn: async ({ id, employeeId, answers }: {
        id: string; employeeId: string; answers: Record<string, unknown>
      }) => (await apiClient.post<SurveyView>(
        `${SURVEYS}/${id}/respond`, { employeeId, answers })).data,
      onSuccess: invalidate,
    }),
    closeSurvey: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<SurveyView>(`${SURVEYS}/${id}/close`)).data,
      onSuccess: invalidate,
    }),
    recognise: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<RecognitionView>(`${BASE}/recognition`, body)).data,
      onSuccess: invalidate,
    }),
    saveSettings: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.put<EngagementSettingView>(`${BASE}/settings`, body)).data,
      onSuccess: invalidate,
    }),
    saveAnnouncement: useMutation({
      mutationFn: async ({ id, body }: { id?: string; body: Record<string, unknown> }) =>
        id
          ? (await apiClient.put<AnnouncementView>(`${BASE}/announcements/${id}`, body)).data
          : (await apiClient.post<AnnouncementView>(`${BASE}/announcements`, body)).data,
      onSuccess: invalidate,
    }),
    /**
     * Archives an announcement.
     *
     * <p>It leaves the feed and keeps its read record — who read it and when is the evidence
     * that a notice reached people, which is the whole point of having recorded it.
     */
    archiveAnnouncement: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<AnnouncementView>(`${BASE}/announcements/${id}/deactivate`)).data,
      onSuccess: invalidate,
    }),
    publishAnnouncement: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<AnnouncementView>(`${BASE}/announcements/${id}/publish`)).data,
      onSuccess: invalidate,
    }),
    markRead: useMutation({
      mutationFn: async ({ id, employeeId }: { id: string; employeeId: string }) =>
        (await apiClient.post<AnnouncementView>(
          `${BASE}/announcements/${id}/read`, { employeeId })).data,
      onSuccess: invalidate,
    }),
  }
}
